import { useEffect, useState } from "react"
import styles from "./edit-product-modal.module.css"
import { useTheme } from "@/components/context/ThemeContext"

export default function EditProductModal({ product, onClose, onSave }) {
  const {theme} = useTheme();
  const [formData, setFormData] = useState({
    name: product.name,
    price: product.price,
    image: product.image,
    details: product.details,
  })

  const [errors, setErrors] = useState({})
	const [preview, setPreview] = useState("")
  const [loading, setLoading] = useState(false)

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

	const handleImage = (e) => {
    const file = e.target.files[0];
    setFormData((prev) => ({
      ...prev,
      image: file,
    }))
		setPreview(URL.createObjectURL(file));
  };

  function getImageSrc(image) {
    if (!image) return null;
  
    // Case 1: Already a single string (URL or complete base64 src)
    if (typeof image === "string") {
      return image;
    }
  
    // Case 2: Segregated: { data: "...", contentType: "..."}
    if (image.data && image.contentType) {
      return `data:${image.contentType};base64,${image.data}`;
    }
  
    return null; // fallback
  }
	

  const validateForm = () => {
    const newErrors = {}
    if (!formData.name.trim()) newErrors.name = "Product name is required"
    if (formData.name.length > 100) newErrors.name = "Name must be less than 100 characters"
    if (!formData.details.trim()) newErrors.description = "Description is required"
    if (formData.details.length > 500) newErrors.description = "Description must be less than 500 characters"
    if (formData.price < 0) newErrors.price = "Price must be a positive number"
    if (!formData.image) newErrors.image = "Image is required"

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      if (validateForm()) {
        await onSave({
          ...product,
          ...formData,
        })
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
      onClose()
    }

  }

  useEffect(() => {
    setPreview(getImageSrc(product.image))
  }, [])
  

  return (
    <div className={`${styles.modalOverlay} ${theme==="dark" ? "dark" : ""}`} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Edit Product</h2>
          <button className={styles.closeBtn} onClick={onClose}>
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGrid}>
            {/* Product Name */}
            <div className={styles.formGroup}>
              <label htmlFor="name" className={styles.label}>
                Product Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`${styles.input} ${errors.name ? styles.inputError : ""}`}
                placeholder="Enter product name"
              />
              {errors.name && <p className={styles.errorText}>{errors.name}</p>}
              <p className={styles.charCount}>{formData.name.length}/100</p>
            </div>

            {/* Price */}
            <div className={styles.formGroup}>
              <label htmlFor="price" className={styles.label}>
                Price (₹) *
              </label>
              <input
                type="number"
                id="price"
                name="price"
                value={formData.price}
                onChange={handleChange}
                className={`${styles.input} ${errors.price ? styles.inputError : ""}`}
                placeholder="0"
                min="0"
              />
              {errors.price && <p className={styles.errorText}>{errors.price}</p>}
            </div>

          {/* Description */}
          <div className={styles.formGroup}>
            <label htmlFor="details" className={styles.label}>
              Details *
            </label>
            <textarea
              id="details"
              name="details"
              value={formData.details}
              onChange={handleChange}
              className={`${styles.textarea} ${errors.description ? styles.inputError : ""}`}
              placeholder="Enter product details"
              rows="3"
            />
            {errors.description && <p className={styles.errorText}>{errors.description}</p>}
            <p className={styles.charCount}>{formData.details.length}/500</p>
          </div>

          {/* Image URL */}
          <div className={styles.formGroup}>
            <label htmlFor="image" className={styles.label}>
              Image URL *
            </label>
            <input
              type="file"
              accept="image/*"
              id="image"
              name="image"
              onChange={(e) => handleImage(e)}
              className={`${styles.input} ${errors.image ? styles.inputError : ""}`}
            />
            {errors.image && <p className={styles.errorText}>{errors.image}</p>}
            {preview && (
              <div className={styles.imagePreview}>
                <img
                  src={preview || "/placeholder.svg"}
                  alt="Preview"
                  onError={(e) => (e.target.style.display = "none")}
                />
              </div>
            )}
          </div>
        </div>

          {/* Form Actions */}
          <div className={styles.formActions}>
            <button type="button" className={styles.cancelBtn} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className={styles.saveBtn}>
              {loading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
